import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import AsyncSelect from "react-select/async";
import { getWithParams } from "../../utils/fetchAPI";
import { FaSearch } from "react-icons/fa";
import { MdClear, MdAssessment } from "react-icons/md";
import PageTitle from "../../components/page-title/pageTitle";
import CustomPagination from "../../components/pagination/CustomPagination";
import Loader from "../../components/loader/loder";

const DoctorWiseAppointmentStats = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [totals, setTotals] = useState({ pending: 0, completed: 0, total: 0 });
    const [error, setError] = useState("");

    const fetchData = async () => {
        setLoading(true);
        setError("");
        try {
            const params = {
                page: currentPage,
                limit: perPage,
            };

            // Only add non-empty parameters
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;
            if (selectedDoctor?.value) params.doctorId = selectedDoctor.value;

            const response = await getWithParams("/dashboard/doctor-wise-appointment-stats", params);

            if (response.data) {
                setData(response.data.stats || []);
                setTotalRows(response.data.total || 0);
                setTotals(response.data.totals || { pending: 0, completed: 0, total: 0 });
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            setError("Failed to fetch data. Please try again.");
            setData([]);
            setTotalRows(0);
            setTotals({ pending: 0, completed: 0, total: 0 });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [currentPage, perPage]);

    // Load doctor options for the searchable dropdown
    const loadDoctors = async (inputValue) => {
        try {
            const res = await getWithParams("/doctor/list-v2", {
                search: inputValue || "",
                per_page: 30,
            });

            return (
                res?.data?.doctors?.map((doc) => ({
                    value: doc._id,
                    label: doc.name,
                })) || []
            );
        } catch (error) {
            console.error("Error loading doctors:", error);
            return [];
        }
    };

    const handleSearch = () => {
        setCurrentPage(1);
        fetchData();
    };

    const handleClearFilters = () => {
        setStartDate("");
        setEndDate("");
        setSelectedDoctor(null);
        setCurrentPage(1);
        // Trigger fetch after clearing
        setTimeout(() => {
            fetchData();
        }, 0);
    };

    const columns = [
        {
            name: '#',
            selector: (row, index) => (currentPage - 1) * perPage + index + 1,
            sortable: true,
            width: '60px',
            center: true,
            compact: true,
        },
        {
            name: "Doctor",
            selector: (row) => row.doctorName || 'N/A',
            sortable: true,
            wrap: true,
            grow: 2,
            minWidth: '160px',
        },
        {
            name: "Pending",
            selector: (row) => row.pending || 0,
            sortable: true,
            grow: 1,
            minWidth: '100px',
            center: true,
            cell: (row) => (
                <div className="font-semibold text-secondary">
                    {row.pending || 0}
                </div>
            ),
        },
        {
            name: "Completed",
            selector: (row) => row.completed || 0,
            sortable: true,
            grow: 1,
            minWidth: '100px',
            center: true,
            cell: (row) => (
                <div className="font-semibold text-green-600">
                    {row.completed || 0}
                </div>
            ),
        },
        {
            name: "Total",
            selector: (row) => row.total || 0,
            sortable: true,
            grow: 1,
            minWidth: '100px',
            center: true,
            cell: (row) => (
                <div className="font-semibold text-primary">
                    {row.total || 0}
                </div>
            ),
        },
    ];

    const customStyles = {
        headCells: {
            style: {
                backgroundColor: '#50b6b5',
                color: 'white',
                fontSize: '0.95rem',
                fontWeight: 'bold',
                paddingLeft: '12px',
                paddingRight: '12px',
                textAlign: 'left',
            },
        },
        rows: {
            style: {
                fontSize: '0.875rem',
                minHeight: '56px',
            },
            stripedStyle: {
                backgroundColor: '#f6f6f6',
            },
            highlightOnHoverStyle: {
                backgroundColor: '#e0e0e0',
                cursor: 'pointer',
            },
        },
        cells: {
            style: {
                paddingLeft: '12px',
                paddingRight: '12px',
            },
        },
        table: {
            style: {
                tableLayout: 'auto',
            },
        },
        tableWrapper: {
            style: {
                display: 'table',
                width: '100%',
            },
        },
    };

    return (
        <div className='flex flex-col w-full gap-5 px-2 sm:px-0'>
            {/* Page Title */}
            <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4'>
                <PageTitle icon={<MdAssessment />} title="Doctor-wise Appointment Stats" />
            </div>

            {/* Filters - Responsive Grid */}
            <div className="bg-white rounded-md p-4 drop-shadow-[0_0px_20px_rgba(203,229,255,0.3)]">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-gray-700">Doctor</label>
                        <AsyncSelect
                            cacheOptions
                            defaultOptions
                            isClearable
                            loadOptions={loadDoctors}
                            value={selectedDoctor}
                            onChange={setSelectedDoctor}
                            placeholder="All doctors"
                            classNamePrefix="react-select"
                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                    </div>
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-gray-700">Start Date</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="text-sm px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:border-teal-800 focus:ring-1 focus:ring-teal-800"
                        />
                    </div>
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-gray-700">End Date</label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="text-sm px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:border-teal-800 focus:ring-1 focus:ring-teal-800"
                        />
                    </div>
                    <button
                        onClick={handleSearch}
                        className="text-sm px-6 py-2 bg-primary text-white rounded-md hover:bg-teal-700 transition-colors duration-200 font-medium shadow-sm hover:shadow-md">
                        <span className="flex items-center justify-center gap-2">
                            <FaSearch className="text-sm" />
                            Apply Filters
                        </span>
                    </button>
                    <button
                        onClick={handleClearFilters}
                        className="text-sm px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors duration-200 font-medium shadow-sm hover:shadow-md">
                        <span className="flex items-center justify-center gap-2">
                            <MdClear className="text-lg" />
                            Clear Filters
                        </span>
                    </button>
                </div>
            </div>

            {/* Summary Totals — reflects the same filters as the list */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                    { label: "Pending", value: totals.pending || 0, valueClass: "text-secondary" },
                    { label: "Completed", value: totals.completed || 0, valueClass: "text-green-600" },
                    { label: "Total", value: totals.total || 0, valueClass: "text-primary" },
                ].map((item) => (
                    <div
                        key={item.label}
                        className="bg-white rounded-md p-4 drop-shadow-[0_0px_20px_rgba(203,229,255,0.3)] flex flex-col gap-1">
                        <p className="text-sm font-medium text-gray-600">{item.label}</p>
                        <p className={`text-2xl font-bold ${item.valueClass}`}>
                            {loading ? "-" : item.value}
                        </p>
                    </div>
                ))}
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
                    <span className="block sm:inline">{error}</span>
                </div>
            )}

            {/* Table Section */}
            {loading ? (
                <div className="flex justify-center items-center py-10">
                    <Loader />
                </div>
            ) : (
                <div className="bg-white rounded-md drop-shadow-[0_0px_20px_rgba(203,229,255,0.3)] overflow-hidden">
                    {data.length > 0 ? (
                        <div className="overflow-x-auto">
                            <DataTable
                                columns={columns}
                                data={data}
                                customStyles={customStyles}
                                highlightOnHover
                                striped
                                responsive
                                dense={false}
                                fixedHeader
                                fixedHeaderScrollHeight="600px"
                                noDataComponent={
                                    <div className="text-center py-10 text-gray-500">
                                        No appointment stats found matching your criteria
                                    </div>
                                }
                            />
                        </div>
                    ) : (
                        <div className="text-center py-20 text-gray-500">
                            <div className="flex flex-col items-center gap-2">
                                <MdAssessment className="text-6xl text-gray-300" />
                                <p className="text-lg font-medium">No data available</p>
                                <p className="text-sm">Try adjusting your filters</p>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Custom Pagination */}
            {!loading && data.length > 0 && (
                <CustomPagination
                    currentPage={currentPage}
                    totalRows={totalRows}
                    perPage={perPage}
                    setCurrentPage={setCurrentPage}
                    setPerPage={setPerPage}
                />
            )}
        </div>
    );
};

export default DoctorWiseAppointmentStats;
